import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import authService from './authService';

class TokenRefreshService {
    private isRefreshing = false;
    private failedQueue: Array<{
        resolve: (value?: any) => void;
        reject: (error?: any) => void;
    }> = [];

    private processQueue(error: any) {
        this.failedQueue.forEach(({ resolve, reject }) => {
            if (error) {
                reject(error);
            } else {
                resolve();
            }
        });
        this.failedQueue = [];
    }

    async refreshToken(): Promise<void> {
        if (!authService.hasSession()) {
            throw new Error('No session to refresh');
        }

        try {
            // The refresh token is an HttpOnly cookie; the API renews the access cookie
            await authService.refreshToken();
        } catch (error) {
            // If refresh token is also invalid, forget the session
            authService.clearSession();
            throw error;
        }
    }

    async handleTokenRefresh(error: AxiosError): Promise<AxiosResponse> {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (this.isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    this.failedQueue.push({ resolve, reject });
                }).then(() => {
                    return axios(originalRequest);
                }).catch((err) => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            this.isRefreshing = true;

            try {
                await this.refreshToken();
                this.processQueue(null);

                // Retry the original request; it carries the renewed access cookie
                return axios(originalRequest);
            } catch (refreshError) {
                this.processQueue(refreshError);
                return Promise.reject(refreshError);
            } finally {
                this.isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
}

export default new TokenRefreshService(); 